import { useContext, useEffect } from 'react';
import { Progress, Spin } from 'antd';
import { useRequest } from 'ahooks';
import { Empty } from '@maxtropy/components';
import {
  apiV2EnergyOverviewGetInspectionAndWorkOrderTaskPost,
  apiV2EnergyOverviewGetInspectionDetailPost,
} from '@maxtropy/device-customer-apis-v2';
import titlePng from '@/assets/images/title_icon.png';
import {
  InspectionCategory,
  InspectionCategoryDisplay,
  InspectionCategoryImg,
  TaskScene,
  TaskSceneColors,
} from '../../../../const';
import styles from './index.module.scss';
import { ShareDataUnityEnergyCockpitMapContext } from '../../../..';
import { getDefaultTime } from '@/pages/WgqAllMaps/utils';
import WorkOrder from '../WorkOrder';
import SubTitle from '@/pages/WgqAllMaps/components/SubTitle';
import bgImg from '../../../../../../imgs/task-bg@2x.png';
import { formatOneDecimalOrDash } from '@/utils/utils';

interface InspectionWorkOrderProps {
  visible?: boolean;
}

const InspectionWorkOrder: React.FC<InspectionWorkOrderProps> = ({ visible }) => {
  const { timeResolution, dateValue } = useContext(ShareDataUnityEnergyCockpitMapContext);

  const {
    data: inspectionAndWorkOrderTaskData,
    loading,
    run: runTask,
  } = useRequest(
    async () => {
      return await apiV2EnergyOverviewGetInspectionAndWorkOrderTaskPost({
        timeResolution,
        fromTime: getDefaultTime(timeResolution!, dateValue)?.startTime,
        toTime: getDefaultTime(timeResolution!, dateValue)?.endTime,
      });
    },
    {
      manual: true,
      ready: !!dateValue && !!timeResolution,
      refreshDeps: [dateValue, timeResolution],
    }
  );
  const {
    data: inspectionDetail,
    loading: detailLoading,
    run: runDetail,
  } = useRequest(
    async () => {
      return await apiV2EnergyOverviewGetInspectionDetailPost({
        timeResolution,
        fromTime: getDefaultTime(timeResolution!, dateValue)?.startTime,
        toTime: getDefaultTime(timeResolution!, dateValue)?.endTime,
      });
    },
    {
      manual: true,
      ready: !!dateValue && !!timeResolution,
      refreshDeps: [dateValue, timeResolution],
    }
  );

  // 弹窗打开时组件挂载，直接调用接口
  useEffect(() => {
    runTask();
    runDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className={styles.inspectionTaskArea}>
        <SubTitle title="工单" img={titlePng} style={{ paddingTop: 12 }} headerStyle={{ paddingBottom: 8 }}>
          <Spin spinning={loading}>
            <div className={styles.totalTask}>
              <img src={bgImg} alt="" />
              总数
              <span className={styles.keyValue}>{Number(inspectionAndWorkOrderTaskData?.workOrderTotalNum ?? 0)}</span>
            </div>
            <div>
              <div className={styles.top_ratio_bar}>
                <div
                  className={styles.top_ratio_bar_item}
                  style={{
                    width: `${Number(inspectionAndWorkOrderTaskData?.electricDetail?.proportion ?? 0) * 100}%`,
                    background: TaskSceneColors[TaskScene.ELECTRICITY],
                  }}
                />
                <div
                  className={styles.top_ratio_bar_item}
                  style={{
                    width: `${Number(inspectionAndWorkOrderTaskData?.waterDetail?.proportion ?? 0) * 100}%`,
                    background: TaskSceneColors[TaskScene.WATER],
                  }}
                />
                <div
                  className={styles.top_ratio_bar_item}
                  style={{
                    width: `${Number(inspectionAndWorkOrderTaskData?.gasDetail?.proportion ?? 0) * 100}%`,
                    background: TaskSceneColors[TaskScene.GAS],
                  }}
                />
              </div>
              <div className={styles.ratio_info}>
                <div className={`${styles.ratio_info_item} ${styles.medium_info_item}`}>
                  <div className={styles.progressInfo}>
                    <Progress
                      type="circle"
                      size={40}
                      percent={100}
                      showInfo={false}
                      trailColor="transparent"
                      strokeColor={{
                        '0%': 'rgba(37,130,183,0.4)',
                        '30%': 'rgba(37,130,183,0.1)',
                        '40%': 'transparent',
                        '60%': 'transparent',
                        '70%': 'rgba(68,120,155,0.1)',
                        '100%': 'rgba(37,130,183,0.4)',
                      }}
                    />
                    <div className={styles.keyValue}>
                      {formatOneDecimalOrDash(inspectionAndWorkOrderTaskData?.electricDetail?.proportion) + '%'}
                    </div>
                  </div>
                  <div className={styles.num}>
                    电：
                    <span style={{ color: '#fff' }}>
                      {inspectionAndWorkOrderTaskData?.electricDetail?.totalNum ?? '--'}
                    </span>
                  </div>
                </div>
                <div className={`${styles.ratio_info_item} ${styles.medium_info_item}`}>
                  <div className={styles.progressInfo}>
                    <Progress
                      type="circle"
                      size={40}
                      percent={100}
                      showInfo={false}
                      trailColor="transparent"
                      strokeColor={{
                        '0%': 'rgba(37,130,183,0.4)',
                        '30%': 'rgba(37,130,183,0.1)',
                        '40%': 'transparent',
                        '60%': 'transparent',
                        '70%': 'rgba(68,120,155,0.1)',
                        '100%': 'rgba(37,130,183,0.4)',
                      }}
                    />
                    <div className={styles.keyValue}>
                      {formatOneDecimalOrDash(inspectionAndWorkOrderTaskData?.waterDetail?.proportion) + '%'}
                    </div>
                  </div>
                  <div className={styles.num}>
                    水：
                    <span style={{ color: '#fff' }}>
                      {inspectionAndWorkOrderTaskData?.waterDetail?.totalNum ?? '--'}
                    </span>
                  </div>
                </div>
                <div className={`${styles.ratio_info_item} ${styles.medium_info_item}`}>
                  <div className={styles.progressInfo}>
                    <Progress
                      type="circle"
                      size={40}
                      percent={100}
                      showInfo={false}
                      trailColor="transparent"
                      strokeColor={{
                        '0%': 'rgba(37,130,183,0.4)',
                        '30%': 'rgba(37,130,183,0.1)',
                        '40%': 'transparent',
                        '60%': 'transparent',
                        '70%': 'rgba(68,120,155,0.1)',
                        '100%': 'rgba(37,130,183,0.4)',
                      }}
                    />
                    <div className={styles.keyValue}>
                      {formatOneDecimalOrDash(inspectionAndWorkOrderTaskData?.gasDetail?.proportion) + '%'}
                    </div>
                  </div>
                  <div className={styles.num}>
                    气：
                    <span style={{ color: '#fff' }}>{inspectionAndWorkOrderTaskData?.gasDetail?.totalNum ?? '--'}</span>
                  </div>
                </div>
              </div>
            </div>
          </Spin>
        </SubTitle>
        <SubTitle title="巡检" img={titlePng} style={{ paddingTop: 12 }} headerStyle={{ paddingBottom: 8 }}>
          <Spin spinning={detailLoading}>
            <div className={styles.inspectionWrapper}>
              <div className={`${styles.inspectionItem} ${styles.inspectionHeader}`}>
                <span className={styles.inspectionCategory}>分类</span>
                <span className={styles.inspectionCount}>次数</span>
                <span className={styles.inspectionProgressInfo}>占比</span>
              </div>
              {!inspectionDetail?.list || inspectionDetail.list.length === 0 ? (
                <Empty style={{ padding: 20 }} />
              ) : (
                inspectionDetail.list.map((item, index) => (
                  <div key={index} className={styles.inspectionItem}>
                    <div className={styles.inspectionCategory}>
                      <img src={InspectionCategoryImg[index as InspectionCategory]} alt="" width={38} />
                      {InspectionCategoryDisplay[index as InspectionCategory]}
                    </div>
                    <div className={styles.inspectionCount}>{item.inspectionNum}</div>
                    <div className={styles.inspectionProgressInfo}>
                      <Progress
                        type="circle"
                        size={28}
                        percent={item.inspectionPercent ? Number(item.inspectionPercent) : 0}
                        trailColor="#4A90E233"
                        showInfo={false}
                        strokeColor="#5BFF60"
                      />
                      {formatOneDecimalOrDash(item.inspectionPercent) + '%'}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Spin>
        </SubTitle>
      </div>
      <WorkOrder />
    </>
  );
};

export default InspectionWorkOrder;
